import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, CalendarCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const today = new Date().toISOString().slice(0, 10);

export default function ParentReservations() {
  const [reservations, setReservations] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [children, setChildren] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ child_id: "", route_id: "", date: today });

  const load = async () => {
    const [r, rt, c] = await Promise.all([api.get("/reservations"), api.get("/routes"), api.get("/children")]);
    setReservations(r.data); setRoutes(rt.data); setChildren(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reservations", form);
      toast.success("Réservation confirmée");
      setShowForm(false); setForm({ child_id: "", route_id: "", date: today });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const cancel = async (id) => {
    if (!confirm("Annuler cette réservation ?")) return;
    await api.delete(`/reservations/${id}`); load();
  };

  return (
    <div className="p-4 pb-6" data-testid="parent-reservations-page">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Agenda</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Réservations</h1>
        </div>
        <button data-testid="parent-new-reservation-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-1 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest">
          <Plus size={14} /> Réserver
        </button>
      </div>

      <div className="space-y-3">
        {reservations.length === 0 ? (
          <div className="border border-zinc-900 p-6 text-sm text-zinc-500 text-center">Aucune réservation.</div>
        ) : reservations.map((r) => {
          const child = children.find((c) => c.id === r.child_id);
          const route = routes.find((x) => x.id === r.route_id);
          return (
            <div key={r.id} className="border border-zinc-900 bg-white p-4 flex items-start gap-3" data-testid={`parent-reservation-${r.id}`}>
              <CalendarCheck size={20} className="mt-1" />
              <div className="flex-1">
                <div className="font-display font-bold text-lg tracking-tight">{child ? `${child.first_name} ${child.last_name}` : "—"}</div>
                <div className="text-xs text-zinc-600">{route?.name} · {r.date}</div>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${r.status === "confirmed" ? "bg-[#FFC60A]" : "bg-zinc-200"}`}>
                {r.status}
              </span>
              {r.status === "confirmed" && (
                <button data-testid={`parent-cancel-reservation-${r.id}`} onClick={() => cancel(r.id)} className="p-2 border border-zinc-900 hover:bg-red-600 hover:text-white hover:border-red-600">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="parent-reservation-modal">
          <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">Nouvelle réservation</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Enfant</label>
                <select data-testid="reservation-form-child" required value={form.child_id} onChange={(e) => setForm({ ...form, child_id: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="">— Sélectionner —</option>
                  {children.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Tournée</label>
                <select data-testid="reservation-form-route" required value={form.route_id} onChange={(e) => setForm({ ...form, route_id: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="">— Sélectionner —</option>
                  {routes.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.date})</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Date</label>
                <input data-testid="reservation-form-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2" />
              </div>
            </div>
            <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-zinc-900 font-semibold">Annuler</button>
              <button data-testid="save-reservation-btn" type="submit" className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 font-bold">Confirmer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
